function createMetas(props, defaultProps, propertiesMap, keyName = 'property') {
  const allowedKeys = Object.keys(propertiesMap);
  const mergedProps = { ...defaultProps, ...props };

  return allowedKeys
    .filter(key => typeof mergedProps[key] !== 'undefined')
    .reduce(
      (acc, key) => [
        ...acc,
        {
          [keyName]: propertiesMap[key],
          content: mergedProps[key]
        }
      ],
      []
    );
}

const createIndexableMetas = ({ indexable }) =>
  indexable
    ? []
    : [
        {
          name: 'robots',
          content: 'noindex, follow'
        }
      ];

const DEFAULT_OG_TYPE = 'spotahome:ad';
const DEFAULT_OG_SITE_NAME = 'Spotahome';
const DEFAULT_OG_LOCALE = 'en_US';

const BASIC_PROPERTIES_MAP = {
  title: 'title',
  description: 'description'
};

const DEFAULT_OPEN_GRAPH_PROPERTIES = {
  type: DEFAULT_OG_TYPE,
  siteName: DEFAULT_OG_SITE_NAME,
  locale: DEFAULT_OG_LOCALE
};

const OPEN_GRAPH_PROPERTIES_MAP = {
  type: 'og:type',
  title: 'og:title',
  description: 'og:description',
  url: 'og:url',
  imageUrl: 'og:image',
  siteName: 'og:site_name',
  locale: 'og:locale'
};

const DEFAULT_TWITTER_SITE = '@Spotahome';

const DEFAULT_TWITTER_PROPERTIES = {
  site: DEFAULT_TWITTER_SITE
};

const TWITTER_PROPERTIES_MAP = {
  card: 'twitter:card',
  site: 'twitter:site',
  title: 'twitter:title',
  description: 'twitter:description',
  imageSrc: 'twitter:image:src',
  url: 'twitter:url'
};

const DEFAULT_THEME_PROPERTIES = {
  themeColor: '#34b196'
};

const THEME_PROPERTIES_MAP = {
  themeColor: 'theme-color'
};

const DEFAULT_MSAPP_PROPERTIES = {
  config: 'none',
  tileSmall: 'https://static.spotahome.com/favicon/smalltile.png',
  tileMedium: 'https://static.spotahome.com/favicon/mediumtile.png',
  tileWide: 'https://static.spotahome.com/favicon/widetile.png',
  tileLarge: 'https://static.spotahome.com/favicon/largetile.png'
};

const MSAPP_PROPERTIES_MAP = {
  validate: 'msvalidate.01',
  config: 'msapplication-config',
  tileSmall: 'msapplication-square70x70logo',
  tileMedium: 'msapplication-square150x150logo',
  tileWide: 'msapplication-wide310x150logo',
  tileLarge: 'msapplication-square310x310logo'
};

const DEFAULT_FACEBOOK_PROPERTIES = {
  siteName: DEFAULT_OG_SITE_NAME,
  locale: DEFAULT_OG_LOCALE
};

const FACEBOOK_PROPERTIES_MAP = {
  title: 'fb:title',
  appId: 'fb:app_id',
  url: 'fb:url',
  imageUrl: 'fb:image',
  siteName: 'fb:site_name',
  locale: 'fb:locale',
  description: 'fb:description'
};

const createBasicMetas = (props = {}) =>
  createMetas(props, {}, BASIC_PROPERTIES_MAP, 'name');

const createOpenGraphMetas = (props = {}) =>
  createMetas(props, DEFAULT_OPEN_GRAPH_PROPERTIES, OPEN_GRAPH_PROPERTIES_MAP);

export const createTwitterMetas = (props = {}) =>
  createMetas(
    props,
    DEFAULT_TWITTER_PROPERTIES,
    TWITTER_PROPERTIES_MAP,
    'name'
  );

export const createThemeMetas = (props = {}) =>
  createMetas(props, DEFAULT_THEME_PROPERTIES, THEME_PROPERTIES_MAP, 'name');

export const createMsAppMetas = (props = {}) =>
  createMetas(props, DEFAULT_MSAPP_PROPERTIES, MSAPP_PROPERTIES_MAP, 'name');

export const createFacebookMetas = (props = {}) =>
  createMetas(props, DEFAULT_FACEBOOK_PROPERTIES, FACEBOOK_PROPERTIES_MAP);

function pageMetadataBuilder({
  basic = {},
  og = {},
  twitter = {},
  fb = {},
  ms = {},
  city,
  cityName
}) {
  const translatedTitle = basic.title?.replace(
    `landingpage.label.${city}`,
    cityName
  );
  const translatedDescription = basic.description?.replace(
    `landingpage.label.${city}`,
    cityName
  );

  return [
    ...createIndexableMetas({
      indexable: basic.indexable
    }),
    ...createBasicMetas({
      title: translatedTitle,
      description: translatedDescription
    }),
    ...createOpenGraphMetas({
      title: translatedTitle,
      description: translatedDescription,
      type: og.type,
      imageUrl: og.imageUrl,
      siteName: DEFAULT_OG_SITE_NAME,
      url: og.url,
      locale: og.locale
    }),
    ...createTwitterMetas({
      card: twitter.card,
      title: translatedTitle,
      description: translatedDescription,
      imageSrc: twitter.imageUrl,
      url: twitter.url
    }),
    ...createFacebookMetas({
      appId: fb.appId,
      title: translatedTitle,
      description: translatedDescription,
      imageUrl: og.imageUrl,
      siteName: DEFAULT_OG_SITE_NAME,
      url: og.url,
      locale: og.locale
    }),
    ...createThemeMetas(),
    ...createMsAppMetas(ms)
  ];
}

export default pageMetadataBuilder;
